import React, { useState, useEffect, useRef } from 'react';

const TermAutocomplete = ({ value, termsList, onUpdate, disabled, style, placeholder, isTextArea, insertField = 'description', onSelectFullItem, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = termsList.filter(item => {
        const term = (value || '').toLowerCase();
        if (!term) return false;
        
        const searchString = `${item.code || ''} ${item.description || ''} ${item.type || ''} ${item[insertField] || ''}`.toLowerCase();
        return searchString.includes(term);
    }).slice(0, 50);

    useEffect(() => {
        setSelectedIndex(-1);
    }, [value, isOpen]);

    const handleKeyDown = (e) => {
        if (!isOpen || filteredOptions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        } else if (e.key === 'Enter') {
            if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
                e.preventDefault();
                handleSelect(filteredOptions[selectedIndex]);
            }
        }
    };

    const handleSelect = (item) => {
        let value;
        if (insertField === 'full') {
            value = item.code ? `${item.code}: ${item.description}` : item.description;
        } else {
            value = item[insertField] || item.code || item.description;
        }
        onUpdate(value.toUpperCase());
        if (onSelectFullItem) {
            onSelectFullItem(item);
        }
        setIsOpen(false);
    };

    const { openUp, listStyle: customListStyle } = props || {};

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
            {isTextArea ? (
                <textarea
                    style={{ ...style, resize: 'none' }}
                    value={value || ''}
                    onChange={(e) => {
                        onUpdate(e.target.value.toUpperCase());
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={placeholder}
                />
            ) : (
                <input
                    type="text"
                    style={{ ...style }}
                    className="form-control form-control-sm"
                    value={value || ''}
                    onChange={(e) => {
                        onUpdate(e.target.value.toUpperCase());
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={placeholder}
                />
            )}
            
            {isOpen && !disabled && value && filteredOptions.length > 0 && (
                <ul style={{
                    position: 'absolute',
                    ...(openUp ? { bottom: '100%', top: 'auto', marginBottom: '4px' } : { top: '100%', left: 0 }),
                    minWidth: '420px',
                    maxWidth: '520px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    maxHeight: '230px',
                    overflowY: 'auto',
                    zIndex: 9999,
                    listStyle: 'none',
                    padding: '4px 0',
                    margin: 0,
                    textAlign: 'left',
                    ...customListStyle
                }}>
                    {filteredOptions.map((item, index) => {
                        const isArmazon = item.category === 'armazon' || (item.type && (item.type.includes('Armazón') || item.type.includes('Luna') || item.type.includes('Insumo') || item.type.includes('Accesorio')));
                        const isProc = item.category === 'procedimiento' || item.type === 'Procedimiento';
                        const badgeBg = isArmazon ? '#dbeafe' : isProc ? '#f3e8ff' : '#dcfce7';
                        const badgeColor = isArmazon ? '#1e40af' : isProc ? '#6b21a8' : '#166534';
                        
                        return (
                            <li 
                                key={item.id || item.code || Math.random()}
                                style={{ 
                                    padding: '8px 12px', 
                                    cursor: 'pointer', 
                                    borderBottom: '1px solid #f3f4f6', 
                                    fontSize: '12px', 
                                    fontFamily: "'Segoe UI', Roboto, sans-serif", 
                                    lineHeight: '1.3',
                                    backgroundColor: index === selectedIndex ? '#f8fafc' : 'transparent',
                                    transition: 'background-color 0.1s'
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(item);
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                                    setSelectedIndex(index);
                                }}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index === selectedIndex ? '#f8fafc' : 'transparent'}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                                    <strong style={{ color: '#0f172a', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {item.code}
                                    </strong>
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                        {item.type && (
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: '700',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                backgroundColor: badgeBg,
                                                color: badgeColor
                                            }}>
                                                {item.type}
                                            </span>
                                        )}
                                        {item.price !== undefined && item.price !== null && (
                                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#059669', backgroundColor: '#ecfdf5', padding: '1px 6px', borderRadius: '4px' }}>
                                                ${parseFloat(item.price).toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {item.description && item.description !== item.code && (
                                    <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                                        {item.description.length > 130 ? item.description.substring(0, 130) + '...' : item.description}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default TermAutocomplete;
